import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {StatusBadge} from 'shells/status-badge/status-badge';
import {Switch} from 'shells/switch/switch';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {GetContextDocuments_contextDocuments as ContextDocument} from 'nutshell-graphql-types';

import {useToggleContextDocumentUsage} from '../../../graphql/hooks';

import {getStatusBadge} from '../../helpers';

import styles from './selected-document-modal.module.css';

type Props = {
    document: ContextDocument;
    canToggle: boolean;
};

export function SelectedDocumentInfoRow(props: Props) {
    const {toggleContextDocumentUsage} = useToggleContextDocumentUsage();

    const handleToggleContextDocumentUsage = (doc: ContextDocument) => {
        toggleContextDocumentUsage({
            documentId: doc.id,
            isEnabled: !doc.isIncluded,
            sourceType: doc.sourceType,
        });
    };

    return (
        <div className='flex gap-24 justify-sb mr-16'>
            <div className={styles['icon-section-container']}>
                <IconSection
                    icon={AiSparkleNewIcon}
                    iconColor={colors.plum}
                    fullWidth={true}
                    marginTopValue={2}
                    gap={4}
                >
                    <Body size='small' color='plum'>
                        Summary
                    </Body>
                </IconSection>
                <Body
                    size='small'
                    color='dark'
                    title={props.document.summary || undefined}
                    isItalic={!props.document.summary}
                    nowrap={true}
                    truncate={{type: 'inline'}}
                >
                    {props.document.summary || 'No summary available'}
                </Body>
            </div>
            <LabelWithContent
                label={
                    <Body color='dark' size='small'>
                        Last Crawled
                    </Body>
                }
                spacing={4}
                content={
                    <Body color='dark' title={props.document.lastCrawledTime}>
                        <RelativeTimestamp datetime={props.document.lastCrawledTime} />
                    </Body>
                }
            />
            <LabelWithContent
                label={
                    <Body color='dark' size='small'>
                        Status
                    </Body>
                }
                spacing={4}
                content={
                    <StatusBadge
                        {...getStatusBadge(
                            props.document.effectiveStatus,
                            props.document.sourceType
                        )}
                    />
                }
            />
            <LabelWithContent
                label={
                    <Body color='dark' size='small'>
                        Enabled
                    </Body>
                }
                spacing={4}
                content={
                    <div>
                        <Switch
                            isOn={props.document.isIncluded}
                            isRounded={true}
                            isSmall={true}
                            onChange={() => handleToggleContextDocumentUsage(props.document)}
                            isDisabled={
                                (!props.canToggle && !props.document.isIncluded) ||
                                props.document.effectiveStatus === 'FAILED'
                            }
                        />
                    </div>
                }
            />
        </div>
    );
}
